/**
 * CRS tier badge - color-coded pill for Capital Readiness Score tier.
 */
import React from 'react';
import { CRSTier } from '../types/capitalReadiness';

interface Props {
  tier: CRSTier | string;
  size?: 'sm' | 'md';
  className?: string;
}

const TIER_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  investment_ready: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300',
    label: 'Investment Ready',
  },
  near_ready: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-300',
    label: 'Near Ready',
  },
  developing: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
    label: 'Developing',
  },
  early_stage: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-300',
    label: 'Early Stage',
  },
  not_ready: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300',
    label: 'Not Ready',
  },
};

export function tierLabel(tier: string): string {
  return (
    TIER_STYLES[tier]?.label ||
    tier.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function tierColorClasses(tier: string): string {
  const s = TIER_STYLES[tier];
  if (!s) return 'bg-gray-100 text-gray-700 border-gray-300';
  return `${s.bg} ${s.text} ${s.border}`;
}

const CRSTierBadge: React.FC<Props> = ({ tier, size = 'md', className = '' }) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${sizeClasses} ${tierColorClasses(
        tier
      )} ${className}`}
    >
      {tierLabel(tier)}
    </span>
  );
};

export default CRSTierBadge;
